import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  CubeIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  ChevronRightIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { useAuth } from '../../providers/AuthProvider';
import type { StockState, StockBuild } from '../../types/inventory';
import { STATE_LABELS, STATE_COLORS, VALID_NEXT_STATES } from '../../types/inventory';

// Single source of truth for the lifecycle state machine lives in types/inventory.ts.
const VALID_NEXT = VALID_NEXT_STATES;

function TimelineEntry({
  fromState,
  toState,
  actor,
  actorName,
  notes,
  date,
  isLatest,
}: {
  fromState: string | null;
  toState: string;
  actor: string | null;
  actorName?: string | null;
  notes: string | null;
  date: string;
  isLatest: boolean;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-3 h-3 rounded-full shrink-0 mt-1 ${isLatest ? 'bg-cyan-400' : 'bg-slate-600'}`} />
        <div className="w-px flex-1 bg-slate-700 mt-1" />
      </div>
      <div className="pb-4 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          {fromState && (
            <>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATE_COLORS[fromState as StockState] ?? 'bg-slate-500/20 text-slate-400'}`}>
                {STATE_LABELS[fromState as StockState] ?? fromState}
              </span>
              <ChevronRightIcon className="w-3 h-3 text-slate-500" />
            </>
          )}
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATE_COLORS[toState as StockState] ?? 'bg-slate-500/20 text-slate-400'}`}>
            {STATE_LABELS[toState as StockState] ?? toState}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <ClockIcon className="w-3 h-3" />
            {new Date(date).toLocaleString('en-NZ')}
          </span>
          {(actorName || actor) && (
            <span className="flex items-center gap-1">
              <UserIcon className="w-3 h-3" />
              {actorName ?? actor}
            </span>
          )}
        </div>
        {notes && <p className="text-xs text-slate-400 mt-1">{notes}</p>}
      </div>
    </div>
  );
}

function BuildCard({ build }: { build: StockBuild }) {
  return (
    <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <WrenchScrewdriverIcon className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium text-white">Gold Build</span>
        </div>
        <span className="text-xs text-slate-400">{new Date(build.builtAt).toLocaleString('en-NZ')}</span>
      </div>
      {build.builtBy && (
        <p className="text-xs text-slate-400">Built by <span className="text-slate-300">{build.builtBy}</span></p>
      )}
      {build.macAddress && (
        <p className="text-xs text-slate-400">MAC <span className="font-mono text-cyan-400">{build.macAddress}</span></p>
      )}
      {build.gitSha && (
        <p className="text-xs text-slate-400">Git <span className="font-mono text-slate-300">{build.gitSha.slice(0, 10)}</span></p>
      )}
      {Object.keys(build.appVersions).length > 0 && (
        <div className="space-y-1">
          <p className="text-xs text-slate-500 uppercase tracking-wide">App Versions</p>
          <div className="grid grid-cols-2 gap-1">
            {Object.entries(build.appVersions).map(([app, ver]) => (
              <div key={app} className="flex items-center justify-between bg-slate-800 rounded px-2 py-1">
                <span className="text-xs text-slate-400">{app}</span>
                <span className="text-xs font-mono text-slate-300">{ver}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {build.notes && <p className="text-xs text-slate-400 italic">{build.notes}</p>}
    </div>
  );
}

function TransitionModal({
  itemId,
  currentState,
  onClose,
}: {
  itemId: string;
  currentState: StockState;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [toState, setToState] = useState<StockState | ''>('');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.adminTransitionStockState(itemId, toState as StockState, notes || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'item', itemId] });
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'stats'] });
      onClose();
    },
  });

  const nextStates = VALID_NEXT[currentState] ?? [];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-sm">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">Change State</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-2">Transition to</label>
            <div className="space-y-1.5">
              {nextStates.map(s => (
                <button
                  key={s}
                  onClick={() => setToState(s)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors border ${
                    toState === s
                      ? 'border-cyan-500 bg-cyan-500/10 text-white'
                      : 'border-slate-700 text-slate-300 hover:border-slate-600'
                  }`}
                >
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mr-2 ${STATE_COLORS[s]}`}>
                    {STATE_LABELS[s]}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes (optional)</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Reason for transition…"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!toState || mutation.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Saving…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AllocateModal({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [orderId, setOrderId] = useState('');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.adminAllocateStockItem(itemId, { order_id: orderId || undefined, notes: notes || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'item', itemId] });
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'stats'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-sm">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">Allocate to Order</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Order ID (optional)</label>
            <input
              value={orderId}
              onChange={e => setOrderId(e.target.value)}
              placeholder="Leave blank if not yet assigned"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Saving…' : 'Allocate'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ShipModal({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('');
  const [notes, setNotes] = useState('');

  const mutation = useMutation({
    mutationFn: () => api.adminShipStockItem(itemId, {
      tracking_ref: tracking || undefined,
      carrier: carrier || undefined,
      notes: notes || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'item', itemId] });
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'stats'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-sm">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">Ship Device</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Carrier</label>
            <input
              value={carrier}
              onChange={e => setCarrier(e.target.value)}
              placeholder="NZ Post, DHL…"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Tracking Reference</label>
            <input
              value={tracking}
              onChange={e => setTracking(e.target.value)}
              placeholder="JD014600007267008767"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Saving…' : 'Mark Shipped'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RegisterBuildModal({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [builtBy, setBuiltBy] = useState(user?.name ?? user?.email ?? '');
  const [gitSha, setGitSha] = useState('');
  const [androidVersion, setAndroidVersion] = useState('');
  const [notes, setNotes] = useState('');
  const [appVersions, setAppVersions] = useState<{ key: string; value: string }[]>([{ key: '', value: '' }]);

  const mutation = useMutation({
    mutationFn: () => {
      const app_versions = appVersions.reduce<Record<string, string>>((acc, row) => {
        const k = row.key.trim();
        if (k) acc[k] = row.value.trim();
        return acc;
      }, {});
      return api.adminRegisterStockBuild(itemId, {
        built_by: builtBy.trim() || null,
        app_versions,
        git_sha: gitSha.trim() || null,
        android_version: androidVersion.trim() || null,
        notes: notes.trim() || null,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'item', itemId] });
      qc.invalidateQueries({ queryKey: ['admin', 'inventory', 'stats'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">Register Build</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Built by</label>
            <input
              value={builtBy}
              onChange={e => setBuiltBy(e.target.value)}
              placeholder="Technician name"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Android version</label>
            <input
              value={androidVersion}
              onChange={e => setAndroidVersion(e.target.value)}
              placeholder="e.g. 13.0"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Git SHA</label>
            <input
              value={gitSha}
              onChange={e => setGitSha(e.target.value)}
              placeholder="Build commit hash"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1.5">App versions</label>
            <div className="space-y-2">
              {appVersions.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={row.key}
                    onChange={e => setAppVersions(rows => rows.map((r, j) => j === i ? { ...r, key: e.target.value } : r))}
                    placeholder="app"
                    className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
                  />
                  <input
                    value={row.value}
                    onChange={e => setAppVersions(rows => rows.map((r, j) => j === i ? { ...r, value: e.target.value } : r))}
                    placeholder="version"
                    className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm font-mono focus:border-cyan-500 focus:outline-none"
                  />
                  <button
                    onClick={() => setAppVersions(rows => rows.length > 1 ? rows.filter((_, j) => j !== i) : rows)}
                    className="p-2 text-slate-500 hover:text-red-400 transition-colors"
                    title="Remove"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={() => setAppVersions(rows => [...rows, { key: '', value: '' }])}
              className="mt-2 flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add app version
            </button>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes</label>
            <input
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional build notes"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {mutation.isPending ? 'Saving…' : 'Register Build'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminInventoryDetailPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [showTransition, setShowTransition] = useState(false);
  const [showAllocate, setShowAllocate] = useState(false);
  const [showShip, setShowShip] = useState(false);
  const [showBuild, setShowBuild] = useState(false);

  const { data: item, isLoading, error } = useQuery({
    queryKey: ['admin', 'inventory', 'item', itemId],
    queryFn: () => api.adminGetStockItem(itemId!),
    enabled: !!itemId,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 py-16 justify-center">
        <ArrowPathIcon className="w-5 h-5 animate-spin" />
        Loading…
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-400 mb-4">Item not found.</p>
        <Link to="/admin/inventory" className="text-cyan-400 hover:text-cyan-300 text-sm">← Back to Inventory</Link>
      </div>
    );
  }

  const attrs = item.attributes as Record<string, string>;
  const nextStates = VALID_NEXT[item.state] ?? [];

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Back */}
      <button
        onClick={() => navigate('/admin/inventory')}
        className="flex items-center gap-2 text-slate-400 hover:text-white text-sm transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Inventory
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white font-mono">{item.serialNumber}</h1>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATE_COLORS[item.state]}`}>
              {STATE_LABELS[item.state]}
            </span>
          </div>
          <p className="text-slate-400 mt-1 text-sm">
            {item.product?.name ?? 'Unknown Product'} · {item.product?.sku}
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {item.state === 'in_build' && (
            <button
              onClick={() => setShowBuild(true)}
              className="flex items-center gap-2 px-3 py-2 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 text-sm rounded-lg border border-cyan-500/30 transition-colors"
            >
              <WrenchScrewdriverIcon className="w-4 h-4" />
              Register Build
            </button>
          )}
          {item.state === 'built' && (
            <button
              onClick={() => setShowAllocate(true)}
              className="flex items-center gap-2 px-3 py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 text-sm rounded-lg border border-purple-500/30 transition-colors"
            >
              <CubeIcon className="w-4 h-4" />
              Allocate
            </button>
          )}
          {item.state === 'allocated' && (
            <button
              onClick={() => setShowShip(true)}
              className="flex items-center gap-2 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 text-sm rounded-lg border border-blue-500/30 transition-colors"
            >
              <TruckIcon className="w-4 h-4" />
              Ship
            </button>
          )}
          {nextStates.length > 0 && (
            <button
              onClick={() => setShowTransition(true)}
              className="flex items-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
            >
              <CheckCircleIcon className="w-4 h-4" />
              Change State
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">

          {/* Hardware Identity */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <WrenchScrewdriverIcon className="w-4 h-4 text-slate-400" />
              Hardware Identity
            </h2>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
              <InfoRow label="Factory ID" value={item.serialNumber} mono />
              {attrs.model && <InfoRow label="Model" value={attrs.model} />}
              {attrs.imei1 && <InfoRow label="IMEI 1" value={attrs.imei1} mono />}
              {attrs.imei2 && <InfoRow label="IMEI 2" value={attrs.imei2} mono />}
              {(item.wifiMacAddress || attrs.mac) && <InfoRow label="WiFi MAC" value={item.wifiMacAddress ?? attrs.mac} mono />}
              {attrs.manufacture_date && <InfoRow label="Manufacture Date" value={attrs.manufacture_date} />}
              {attrs.mkt && <InfoRow label="Market Code" value={attrs.mkt} />}
            </div>
          </div>

          {/* Builds */}
          {item.builds && item.builds.length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <WrenchScrewdriverIcon className="w-4 h-4 text-cyan-400" />
                Build Records
              </h2>
              <div className="space-y-3">
                {item.builds.map(b => <BuildCard key={b.id} build={b} />)}
              </div>
            </div>
          )}

          {/* Allocation */}
          {item.latestAllocation && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <TruckIcon className="w-4 h-4 text-slate-400" />
                Shipping
              </h2>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2.5">
                {item.latestAllocation.orderId && <InfoRow label="Order ID" value={item.latestAllocation.orderId} mono />}
                {item.latestAllocation.carrier && <InfoRow label="Carrier" value={item.latestAllocation.carrier} />}
                {item.latestAllocation.trackingRef && <InfoRow label="Tracking" value={item.latestAllocation.trackingRef} mono />}
                {item.latestAllocation.shippedAt && <InfoRow label="Shipped" value={new Date(item.latestAllocation.shippedAt).toLocaleString('en-NZ')} />}
              </div>
            </div>
          )}
        </div>

        {/* Right: Timeline + Meta */}
        <div className="space-y-6">
          {/* Meta */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
            <h2 className="text-sm font-semibold text-white mb-4">Receipt</h2>
            <div className="space-y-2.5">
              <InfoRow label="Batch" value={item.batchTag ?? '—'} />
              <InfoRow label="Received" value={new Date(item.receivedAt).toLocaleString('en-NZ')} />
              {item.receivedByName && <InfoRow label="Received by" value={item.receivedByName} />}
              {item.notes && <InfoRow label="Notes" value={item.notes} />}
            </div>
          </div>

          {/* State Timeline */}
          {item.stateHistory && item.stateHistory.length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
              <h2 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <ClockIcon className="w-4 h-4 text-slate-400" />
                History
              </h2>
              <div>
                {item.stateHistory.map((entry, i) => (
                  <TimelineEntry
                    key={entry.id}
                    fromState={entry.fromState}
                    toState={entry.toState}
                    actor={entry.actor}
                    actorName={entry.actorName}
                    notes={entry.notes}
                    date={entry.createdAt}
                    isLatest={i === 0}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showTransition && (
        <TransitionModal
          itemId={item.id}
          currentState={item.state}
          onClose={() => setShowTransition(false)}
        />
      )}
      {showAllocate && (
        <AllocateModal itemId={item.id} onClose={() => setShowAllocate(false)} />
      )}
      {showShip && (
        <ShipModal itemId={item.id} onClose={() => setShowShip(false)} />
      )}
      {showBuild && (
        <RegisterBuildModal itemId={item.id} onClose={() => setShowBuild(false)} />
      )}
    </div>
  );
}

function InfoRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className={`text-sm text-slate-200 mt-0.5 ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  );
}
